import { Modal } from "components";
import { ButtonPrimary, ButtonSecondary } from "components/Atoms/Button";
import { PhosphorSparkle } from "components/Atoms/PhosphorIcon";
import { FileDropzone } from "components/FileUpload/FileDropzone";
import { getAcceptForFileType } from "shared/utils/fileTypeUtils";
import { Loading } from "components/panels";
import ArtistList from "components/Picker/ArtistPicker/ArtistList";
import { Tabs } from "components/Tabs/Tabs";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useContext, useEffect, useState } from "react";
import { uploadFileToCoreApi } from "utilities/fileUploadUtils";

interface Props {
  initialArtist?: server.dto.ArtistSmall | null;
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

type NewFlightWithAIAssistantTabs = "artist" | "uploadFile";
type NewFlightWithAIAssistantFileOrPaste = "file" | "paste";
const newFlightWithAIAssistantModalHeigth = 600;
const newFlightWithAIAssistantArtistListHeight =
  newFlightWithAIAssistantModalHeigth -
  24 - // modal title
  58 - // tabs
  30 - // buttons
  48 - // Three gaps of 16px each
  48; // padding
const newFlightWithAIAssistantContentHeight =
  newFlightWithAIAssistantArtistListHeight -
  16 -
  24;
// 16px is the height of row, 15px is padding
const newFlightWithAIAssistantNumberOfRows = (newFlightWithAIAssistantContentHeight - 15) / 16 - 1;

export const NewFlightWithAIAssistantModal = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState<NewFlightWithAIAssistantTabs>(props.initialArtist ? "uploadFile" : "artist");
  const [fileOrPaste, setFileOrPaste] = useState<NewFlightWithAIAssistantFileOrPaste>("file");
  const [uploadError, setUploadError] = useState<string>("");
  const [artist, setArtist] = useState<server.dto.ArtistSmall | null>(props.initialArtist);
  const [text, setText] = useState<string>("");
  const planningContext = useContext(PlanningContext);

  
  useEffect(() => {
    if (!props.isOpen) {
      setIsLoading(false);
      setSelectedTab("artist");
      setFileOrPaste("file");
      setUploadError("");
      setArtist(null);
      setText("");
    } else {
      setSelectedTab(props.initialArtist ? "uploadFile" : "artist");
      setArtist(props.initialArtist || null);
    }
  }, [props.isOpen]);

  const submitText = async () => {
    setIsLoading(true);

    const res = await window.CoreApi.Extractor.StartSessionWithText({
      artistId: artist.artistId,
      linkToShowId: planningContext?.opts?.fromShowId,
      text,
    });

    if (res.data.hasErrors) {
      setIsLoading(false);
      setUploadError(res.data.errors[0].message);
      return;
    }

    props.onClose();
    props.onUploadSuccess();
  }

  const submitFile = async (files: File[]) => {
    if (files.length !== 1) {
      return;
    }
    setIsLoading(true);

    const res = await uploadFileToCoreApi(
      window.CoreApi.Extractor.StartSessionWithFile,
      window.CoreApi.Extractor.StartSessionWithFile.absoluteUrl(),
      {
        artistId: artist.artistId,
        // Don't include `linkToShowId` if it's null. Otherwise it causes errors on the server
        ...(planningContext?.opts?.fromShowId != null && {
          linkToShowId: planningContext.opts.fromShowId,
        }),
        file: files[0],
      },
      {
        fileType: "any",
        timeout: 60000,
      }
    );

    if (res.data.hasErrors) {
      setIsLoading(false);
      setUploadError(res.data.errors[0].message);
      return;
    }

    props.onClose();
    props.onUploadSuccess();
  }

  return <Modal
    isOpen={props.isOpen}
    title={<div className="flex align-end gap">
      <div style={{ height: 24 }}><PhosphorSparkle iconSize="large" /></div>
      <h1>Import flight plan</h1>
    </div>}
    handleClose={props.onClose}
    width="small"
    gap="larger"
    height={600}
  >
    <Tabs
      options={[
        { label: RESX.Artist.SelectArtist, value: "artist" },
        { label: "Provide data", value: "uploadFile" },
      ]}
      value={selectedTab}
      onChange={v => {
        if (v === "uploadFile" && !artist) {
          return;
        }
        setSelectedTab(v)
      }}
    />

    {selectedTab === "artist" && <div style={{
      height: newFlightWithAIAssistantArtistListHeight,
    }} className="flex-column gap grow1">
      <ArtistList
        hideFilter1
        securityScope="limitByUserAccess"
        onItemSelected={(artist) => {
          setArtist(artist);
          setSelectedTab("uploadFile");
        }}
      />
    </div>}

    {selectedTab === "uploadFile" && <div className="padding flex gap">
      <div style={{ width: 160 }}>{site.currentEnvironment.functionGroups.artist.singular}</div>
      <div>{artist.name}</div>
    </div>}

    {selectedTab === "uploadFile" && <div className="flex" style={{
      height: newFlightWithAIAssistantContentHeight,
    }}>
      {fileOrPaste === "file" && <>
        {isLoading && <div className="grow1 flex justify-center align-center"><Loading /></div>}
        {!isLoading && <FileDropzone
          error={uploadError !== ""}
          uploadLabel="Click to upload a flight plan"
          maxFiles={1}
          accept={getAcceptForFileType("any")}
          onDrop={submitFile}
          onDropReject={(fileRejections) => {
            setUploadError(fileRejections[0].errors[0].message);
          }}
          maxSizeBytes={serverReference.constants.file.maximumTravelFileContentLength}
          iconType="file"
        />}
      </>}
      {fileOrPaste === "paste" && <OptionalTextAreaInput
        focusOnMount
        style={{
          resize: "none",
        }}
        rows={newFlightWithAIAssistantNumberOfRows}
        value={text}
        onChange={text => setText(text)}
        maxLength={serverReference.constants.extractorSession.maxTextLength}
      />}
    </div>}

    {selectedTab === "artist" && <div className="flex justify-space-between">
      <div />
      <ButtonPrimary label={RESX.Buttons.btnNew} disabled={!artist} />
    </div>}
    {selectedTab === "uploadFile" && <div className="flex justify-space-between">
      <ButtonSecondary
        label={fileOrPaste === "paste" ? "Upload PDF" : "Paste text"}
        onClick={() => setFileOrPaste(fileOrPaste === "file" ? "paste" : "file")}
      />
      {fileOrPaste === "paste" && <ButtonPrimary
        loading={isLoading}
        disabled={!text}
        label="Submit"
        onClick={submitText}
      />}
    </div>}
  </Modal>
}